import {
  cloneElement,
  forwardRef,
  isValidElement,
  useRef,
  type CSSProperties,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
  type Ref,
} from "react";
import { Transition } from "react-transition-group";
import type { TransitionStatus } from "react-transition-group";
import { Dialog, useMediaQuery, useTheme, type Breakpoint, type TransitionProps } from "~/common/mui";

interface ResponsiveDialogDrawerProps {
  open: boolean;
  onClose: (event?: MouseEvent) => void;
  children?: ReactNode;
  maxWidth?: false | Breakpoint | undefined;
}

/**
 * Extends MUI's TransitionProps so the component is structurally assignable to
 * Dialog's TransitionComponent prop. Only `children` is added on top — everything
 * else (in, timeout, easing, onEnter*, …) is inherited.
 */
interface CustomTransitionProps extends TransitionProps {
  children: ReactElement;
}

/**
 * A single, stable Transition component that never changes its React element type.
 * It picks its animation purely through CSS applied to the child via cloneElement:
 *   – Phone     → slide up from the bottom
 *   – Otherwise → fade in
 *
 * Because the component reference never changes, React keeps the entire Dialog
 * subtree alive when the breakpoint crosses the phone boundary.
 */
const CustomTransition = forwardRef(function CustomTransition(
  {
    children,
    in: inProp = false,
    timeout,
    appear = true,
    onEnter,
    onEntering,
    onEntered,
    onExit,
    onExiting,
    onExited,
    style,
  }: CustomTransitionProps,
  ref: Ref<unknown>,
) {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.only("phone"));

  // nodeRef lets react-transition-group avoid the deprecated findDOMNode call.
  const nodeRef = useRef<HTMLElement | null>(null);

  const enterMs = (typeof timeout === "object" ? timeout.enter : timeout) ?? theme.transitions.duration.enteringScreen;
  const exitMs = (typeof timeout === "object" ? timeout.exit : timeout) ?? theme.transitions.duration.leavingScreen;

  const enterEasing = theme.transitions.easing.easeOut;
  const exitEasing = theme.transitions.easing.sharp;

  /**
   * Phone  — slides from translateY(100%) → translateY(0) on enter; reverses on exit.
   * Desktop — fades from opacity 0 → 1 on enter; reverses on exit.
   *
   * The `transition` CSS property is only present during entering/exiting so there
   * is no unwanted animation when the element is first inserted (exited state).
   */
  const stateStyles: Record<TransitionStatus, CSSProperties> = {
    entering: isPhone
      ? {
          transform: "translateY(0)",
          opacity: 1,
          transition: `transform ${enterMs}ms ${enterEasing}`,
        }
      : {
          opacity: 1,
          transition: `opacity ${enterMs}ms ${enterEasing}`,
        },
    entered: isPhone ? { transform: "translateY(0)", opacity: 1 } : { opacity: 1 },
    exiting: isPhone
      ? {
          transform: "translateY(100%)",
          opacity: 1,
          transition: `transform ${exitMs}ms ${exitEasing}`,
        }
      : {
          opacity: 0,
          transition: `opacity ${exitMs}ms ${exitEasing}`,
        },
    exited: isPhone ? { transform: "translateY(100%)", opacity: 0 } : { opacity: 0 },
    unmounted: {},
  };

  if (!isValidElement(children)) {
    return null;
  }

  /**
   * react-transition-group omits the `node` argument when `nodeRef` is used,
   * but MUI's TransitionProps callbacks expect `(node, isAppearing?)`.
   * These wrappers pull the node from nodeRef and bridge the two signatures.
   */
  const wrap2 = (cb?: (node: HTMLElement, isAppearing: boolean) => void) =>
    cb && nodeRef.current ? (isAppearing: boolean) => cb(nodeRef.current!, isAppearing) : undefined;
  const wrap1 = (cb?: (node: HTMLElement) => void) => (cb && nodeRef.current ? () => cb(nodeRef.current!) : undefined);

  return (
    <Transition
      appear={appear}
      in={inProp}
      nodeRef={nodeRef}
      timeout={{ enter: enterMs, exit: exitMs }}
      onEnter={wrap2(onEnter)}
      onEntering={wrap2(onEntering)}
      onEntered={wrap2(onEntered)}
      onExit={wrap1(onExit)}
      onExiting={wrap1(onExiting)}
      onExited={wrap1(onExited)}
    >
      {(state: TransitionStatus) =>
        cloneElement(children as ReactElement<{ ref?: Ref<unknown>; style?: CSSProperties }>, {
          // Forward the ref so MUI can still reach the Paper DOM node.
          ref: ref ?? nodeRef,
          style: {
            // Keep it invisible (but rendered) while fully exited, matching
            // the behaviour of MUI's own Fade/Slide.
            visibility: state === "exited" && !inProp ? "hidden" : undefined,
            ...stateStyles[state],
            // Caller-provided style and the child's own style take precedence.
            ...style,
            ...(children.props as { style?: CSSProperties }).style,
          },
        })
      }
    </Transition>
  );
});

export default function ResponsiveDialogDrawer({ open, onClose, children, maxWidth }: ResponsiveDialogDrawerProps) {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.only("phone"));

  return (
    <Dialog
      open={open}
      onClose={(event) => onClose(event as MouseEvent)}
      TransitionComponent={CustomTransition}
      fullWidth
      maxWidth={maxWidth}
      sx={
        isPhone
          ? {
              "& .MuiDialog-container": {
                alignItems: "flex-end",
              },
            }
          : undefined
      }
      PaperProps={{
        sx: isPhone
          ? {
              m: 0,
              width: "100%",
              maxWidth: "100%",
              borderRadius: 0,
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
              borderTopLeftRadius: "28px",
              borderTopRightRadius: "28px",
            }
          : {
              borderRadius: "28px",
            },
      }}
    >
      {children}
    </Dialog>
  );
}
